import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import {
  decodeTime,
  formatTime,
} from './EventTools'

import styles from '../../../../css/scheduler.module.css'

const getHrDropdown = () => {
  const array = []
  for (let i = 0; i < 168; i++) {
    array.push({ key: i, value: i, str: `${i} Hrs` })
  }
  return array
}
const getMinDropdown = () => {
  const array = []
  for (let i = 0; i < 60; i++) {
    array.push({ key: i, value: i, str: `${i} Mins` })
  }
  return array
}

const getWeekDays = () => [
  { str: 'Sunday', value: 0, key: 0 },
  { str: 'Monday', value: 1, key: 1 },
  { str: 'Tuesday', value: 2, key: 2 },
  { str: 'Wednesday', value: 3, key: 3 },
  { str: 'Thursday', value: 4, key: 4 },
  { str: 'Friday', value: 5, key: 5 },
  { str: 'Saturday', value: 6, key: 6 },
]

function EventForm ({
  updateIndex, events, eventFormDay = (new Date()).getDay(), addEvent, updateEvent, removeEvent,
}) {
  const [state, setState] = useState(() => {
    let day = eventFormDay
    let hr = (new Date()).getHours()
    let min = Math.ceil(((new Date()).getMinutes() + 1) / 15) * 15
    if (min >= 60) {
      min = 15
      hr += 1
    }
    let startTime = formatTime(hr, min)
    let duration = 120

    if (updateIndex >= 0 && Array.isArray(events) && updateIndex < events.length) {
      const event = decodeTime(events[updateIndex], (new Date()).getTimezoneOffset(), updateIndex)
      day = event.start.day
      hr = event.start.hr
      min = event.start.min
      startTime = formatTime(hr, min)
      duration = event.duration.mins
    }

    const durationHr = Math.floor(duration / 60)
    const durationMin = duration % 60

    return {
      day,
      hr,
      min,
      startTime,
      startTimeValid: true,
      duration,
      durationHr,
      durationMin,
      durationValid: duration > 0,
    }
  })

  const createEvent = useCallback(() => {
    const {
      day, hr, min, duration,
    } = state
    addEvent({
      day, hr, min, duration,
    })
  }, [addEvent, state])

  const modifyEvent = useCallback((updateKey) => {
    const {
      day, hr, min, duration,
    } = state
    updateEvent(updateKey, {
      day, hr, min, duration,
    })
  }, [updateEvent, state])

  const deleteEvent = useCallback((updateKey) => {
    removeEvent(updateKey)
  }, [removeEvent])

  const handleStartTime = useCallback((event) => {
    setState((prev) => ({ ...prev, hr: Number(event.target.value) }))
  }, [])

  const handleStartTimeChange = useCallback((event) => {
    const time = event.target.value
    if (!time || typeof time !== 'string') return
    if (time.length > 8) {
      setState((prev) => ({
        ...prev,
        startTime: event.target.value,
        startTimeValid: false,
      }))
      return
    }

    let startTimeValid = false
    if (
      time.match(/^([0-9]|0[0-9]|1[0-9]|2[0-3]):?[0-5][0-9]$/)
    ) {
      const hr = Number(time.slice(0, time.length < 4 ? 1 : 2))
      const min = Number(time.slice(-2))
      startTimeValid = true
      if (hr >= 0 && hr <= 23 && min >= 0 && min <= 59) {
        setState((prev) => ({
          ...prev,
          startTime: time,
          startTimeValid,
          hr,
          min,
        }))
        return
      }
    }
    setState((prev) => ({
      ...prev,
      startTime: event.target.value,
      startTimeValid: false,
    }))
  }, [])

  const handleDurationHrChange = useCallback((event) => {
    const durationHr = Number(event.target.value)
    if (durationHr >= 0 && durationHr < 168) {
      setState((prev) => ({
        ...prev,
        durationHr,
        duration: (durationHr * 60 + prev.durationMin),
        durationValid: (durationHr * 60 + prev.durationMin) > 0,
      }))
    }
  }, [])

  const handleDurationMinChange = useCallback((event) => {
    const durationMin = Number(event.target.value)
    if (durationMin >= 0 && durationMin < 60) {
      setState((prev) => ({
        ...prev,
        durationMin,
        duration: (prev.durationHr * 60 + durationMin),
        durationValid: (prev.durationHr * 60 + durationMin) > 0,
      }))
    }
  }, [])

  const handleDateChange = useCallback((event) => {
    setState((prev) => ({ ...prev, day: Number(event.target.value) }))
  }, [])

  const decoded = useMemo(() => {
    const {
      day, hr, min, duration,
    } = state
    return decodeTime(
      day * 24 * 60 + hr * 60 + min + duration,
      0
    )
  }, [state])

  const {
    day,
    hr,
    min,
    duration,
    startTime,
    startTimeValid,
    durationHr,
    durationMin,
    durationValid,
  } = state

  return (
    <div className={`${styles.event_form} ${styles.inputArea}`}>
      <div className={styles.event_inputs}>
        <div className={styles.event_inputs_label}>
          Start Day
          <div className={styles.event_day_select}>
            <select value={day} onChange={handleDateChange}>
              {getWeekDays().map((d) => (
                (<option key={d.key} value={d.value}>{d.str}</option>)
              ))}
            </select>
          </div>
        </div>
        <div className={styles.event_inputs_label}>
          Start Time
          <input type='time' value={startTime} onChange={handleStartTimeChange} />
        </div>
        <div className={styles.event_inputs_label}>
          Duration
          <div className={styles.duration}>
            <select id='duration_hr' value={durationHr} onChange={handleDurationHrChange}>
              {getHrDropdown().map((d) => (
                (<option key={d.key} value={d.value}>{d.str}</option>)
              ))}
            </select>
            &nbsp;
            <select id='duration_min' value={durationMin} onChange={handleDurationMinChange}>
              {getMinDropdown().map((d) => (
                (<option key={d.key} value={d.value}>{d.str}</option>)
              ))}
            </select>
          </div>
          <div>
            <i className={`fa fa-clock-o ${styles.timeClock}`} />
            <span className={styles.timeForecast}>
&nbsp;Ends
              {decoded.stop.human}
            </span>
          </div>
        </div>
      </div>
      {startTimeValid && durationValid
        ? <button type='button' className={styles.save_button} onClick={(updateIndex >= 0) ? () => { modifyEvent(updateIndex) } : createEvent}>SAVE</button>
        : ''}
      {updateIndex >= 0
        ? <button type='button' className={styles.remove_button} onClick={() => { deleteEvent(updateIndex) }}>DELETE</button>
        : ''}
    </div>
  )
}

EventForm.propTypes = {
  addEvent: PropTypes.func.isRequired,
  events: PropTypes.array.isRequired,
  updateEvent: PropTypes.func.isRequired,
  removeEvent: PropTypes.func.isRequired,
  updateIndex: PropTypes.number.isRequired,
  eventFormDay: PropTypes.number.isRequired,
}

export default EventForm
